import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Flower2, 
  TreePine, 
  Leaf, 
  Scissors, 
  Droplets, 
  Mountain,
  ArrowRight
} from "lucide-react";

const ServicesSection = () => {
  const serviceCategories = [
    {
      title: "Landscaping Services",
      icon: TreePine,
      services: [
        { name: "Landscape Design", description: "Custom designs tailored to your property" },
        { name: "Lawn Installation", description: "New sod or seed installation" },
        { name: "Garden Planting", description: "Flowers, shrubs, and trees" },
      ],
      image: "https://images.unsplash.com/photo-1689728318937-17d24bc0a65c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBnYXJkZW5lciUyMHdvcmtpbmclMjBpbiUyMGdhcmRlbiUyMGxhd24lMjBtb3dlcnxlbnwwfHx8fDE3NzI3MDYyNTN8MA&ixlib=rb-4.1.0&q=85",
    },
    {
      title: "Garden Maintenance",
      icon: Leaf,
      services: [
        { name: "Lawn Mowing", description: "Regular scheduled mowing service" },
        { name: "Hedge & Shrub Trimming", description: "Keep hedges neat and tidy" },
        { name: "Seasonal Garden Cleanup", description: "Spring and fall cleanup services" },
      ],
      image: "https://images.unsplash.com/photo-1728706613021-e447801e1ea6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBnYXJkZW5lciUyMHdvcmtpbmclMjBpbiUyMGdhcmRlbiUyMGxhd24lMjBtb3dlcnxlbnwwfHx8fDE3NzI3MDYyNTN8MA&ixlib=rb-4.1.0&q=85",
    },
    {
      title: "Additional Services",
      icon: Droplets,
      services: [
        { name: "Irrigation Setup", description: "Efficient watering systems" },
        { name: "Mulching", description: "Protect plants and retain moisture" },
        { name: "Soil Preparation", description: "Testing and amendment services" },
      ],
      image: "https://images.unsplash.com/photo-1762608675427-09ac2dbd1540?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBiYWNreWFyZCUyMHBhdGlvJTIwZGVzaWduJTIwcGF2aW5nJTIwbGFuZHNjYXBpbmd8ZW58MHx8fHwxNzcyNzA2MjUzfDA&ixlib=rb-4.1.0&q=85",
    },
  ];

  return (
    <section
      data-testid="services-section"
      className="section-padding bg-[#FAFAF8] relative"
    >
      <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-[#C2410C] font-medium text-sm mb-4">
            <Flower2 className="w-4 h-4" />
            Our Services
          </span>
          <h2
            data-testid="services-headline"
            className="font-heading text-3xl md:text-4xl lg:text-5xl text-[#1A2C22] font-bold mb-6"
          >
            Professional Landscaping Solutions
          </h2>
          <p className="text-[#4B5563] text-base md:text-lg max-w-2xl mx-auto">
            From design to maintenance, we offer comprehensive landscaping services to keep your outdoor space beautiful year-round.
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {serviceCategories.map((category, index) => (
            <Card
              key={index}
              data-testid={`service-card-${index}`}
              className="service-card bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden group"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={category.image}
                  alt={category.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm p-3 rounded-xl">
                  <category.icon className="w-6 h-6 text-[#2D5A27]" />
                </div>
              </div>

              <CardContent className="p-6">
                <h3 className="font-heading text-xl font-semibold text-[#1A2C22] mb-4">
                  {category.title}
                </h3>
                <ul className="space-y-3 mb-6">
                  {category.services.map((service, sIndex) => (
                    <li key={sIndex} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-[#2D5A27] rounded-full mt-2 flex-shrink-0" />
                      <div>
                        <p className="text-[#1A2C22] font-medium text-sm">
                          {service.name}
                        </p>
                        <p className="text-[#6B7280] text-xs">
                          {service.description}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
                <Link to="/services">
                  <Button
                    variant="ghost"
                    data-testid={`service-learn-more-${index}`}
                    className="text-[#2D5A27] hover:text-[#1E3F1B] hover:bg-[#F3F4F1] p-0 h-auto font-medium group"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pricing Note */}
        <div className="mt-12 text-center">
          <p className="text-[#6B7280] text-sm bg-[#F3F4F1] inline-block px-6 py-3 rounded-full">
            Service pricing varies depending on property size, materials, and project scope.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
